import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const emojis = [
    "🐶", "🚗", "🦄", "🎈", "🚀", "🐘", "🍎", "🏀", "📕", "⚽",
    "🍕", "🎸", "🚢", "🐱", "🌞", "🍔", "🚲", "🏕", "📡", "🔑",
    "🐳", "🌵", "🎠", "🍩", "🧀", "🚂", "🐙", "🦆", "🏖", "📖",
    "🍉", "🌊", "🎻", "🌈", "🍟", "⚡", "🎯", "🐟", "🚜", "🦅",
    "📱", "📚", "🥕", "🦖", "🎃", "🐬", "🎁", "🍇", "🚀", "🛶",
    "🦜", "🏹", "🐧", "🦊", "🏰", "🚦", "🎠", "🌼", "💎", "🗻",
    "🦄", "📌", "🥦", "🎷", "🔬", "🐼", "📎", "🦕", "🍵", "⚔️",
    "🦉", "🌙", "🎭", "🏜", "📀", "🦢", "🐎", "🧊", "🎇", "🏄",
    "🚁", "🦋", "🦔", "🍪", "🛰", "🐝", "🎤", "🦑", "🏎", "🍍"
];

const MoreOrLessGame: React.FC = () => {
    const [setA, setSetA] = useState<string[]>([]);
    const [setB, setSetB] = useState<string[]>([]);
    const [questionType, setQuestionType] = useState<string>("more"); // "more" or "less"
    const [currentEmoji, setCurrentEmoji] = useState<string>("🍎");
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "More and Less – LKG Pre-Math Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const generateNewQuestion = () => {
        let sizeA, sizeB;

        // Ensure set sizes are different
        do {
            sizeA = Math.floor(Math.random() * 6) + 1; // Between 1 and 6
            sizeB = Math.floor(Math.random() * 6) + 1; // Between 1 and 6
        } while (sizeA === sizeB); // Keep generating until they are different

        // Choose a random emoji for the question
        const selectedEmoji = emojis[Math.floor(Math.random() * emojis.length)];
        setCurrentEmoji(selectedEmoji);

        // Create emoji sets
        const newSetA = new Array(sizeA).fill(selectedEmoji);
        const newSetB = new Array(sizeB).fill(selectedEmoji);

        // Decide if the question is "more" or "less"
        const isMoreQuestion = Math.random() > 0.5;
        setQuestionType(isMoreQuestion ? "more" : "less");

        // Determine the correct answer
        const correct = sizeA > sizeB ? "A" : "B";
        setCorrectAnswer(isMoreQuestion ? correct : correct === "A" ? "B" : "A");

        setSetA(newSetA);
        setSetB(newSetB);
        setMessage("");
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Teach the concept of more and less to LKG students with engaging activities on Mama’s Guidance—designed to build foundational math and comparison skills through interactive visuals and playful learning.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>More and Less</h1>

                                    <p>
                                        Teach your child one of the most essential pre-math skills — understanding <strong>more and less</strong>. This worksheet is designed for LKG students to practice <strong>visual counting, comparison, and reasoning</strong> in a simple and playful way.
                                    </p>

                                    <h2>🔢 What Does More and Less Mean?</h2>
                                    <p>
                                        “<strong>More</strong>” means a greater number or amount, and “<strong>Less</strong>” means a smaller quantity. By comparing groups of objects, children start to build their number sense and prepare for addition and subtraction.
                                    </p>

                                    <h2>🧩 What’s in the Worksheet?</h2>
                                    <ul>
                                        <li>Pictures showing sets of items with different quantities</li>
                                        <li>Circle the group that has more or less</li>
                                        <li>Match items with <strong>“more” or “less”</strong> labels</li>
                                        <li>Count and color the set that meets the question criteria</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Compare two or more groups based on quantity</li>
                                        <li>Develop vocabulary (<strong>more, less, fewer</strong>)</li>
                                        <li>Enhance early counting skills (1–10)</li>
                                        <li>Build a strong foundation for <strong>addition and subtraction</strong></li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Ask your child to count items in each group shown.</li>
                                        <li>Guide them to circle the group with <strong>“more” or “less.”</strong></li>
                                        <li>Use real objects like blocks, spoons, or fruits to recreate the questions.</li>
                                        <li>Say phrases like <strong>“Which plate has more?”</strong> or <strong>“Which bowl has less?”</strong> to build familiarity.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Big - Small</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Full - Empty</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Concept Is Important</h2>
                                    <p>
                                        <strong>“More and Less”</strong> is not just about numbers — it’s about <strong>logical thinking and decision-making</strong>. Mastery of this concept supports early problem-solving and strengthens number comparison — skills that are key for math success in higher grades.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>More and Less</h1>

                                <p>
                                    Teach your child one of the most essential pre-math skills — understanding <strong>more and less</strong>. This worksheet is designed for LKG students to practice <strong>visual counting, comparison, and reasoning</strong> in a simple and playful way.
                                </p>

                                <h2>🔢 What Does More and Less Mean?</h2>
                                <p>
                                    “<strong>More</strong>” means a greater number or amount, and “<strong>Less</strong>” means a smaller quantity. By comparing groups of objects, children start to build their number sense and prepare for addition and subtraction.
                                </p>

                                <h2>🧩 What’s in the Worksheet?</h2>
                                <ul>
                                    <li>Pictures showing sets of items with different quantities</li>
                                    <li>Circle the group that has more or less</li>
                                    <li>Match items with <strong>“more” or “less”</strong> labels</li>
                                    <li>Count and color the set that meets the question criteria</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Compare two or more groups based on quantity</li>
                                    <li>Develop vocabulary (more, less, fewer)</li>
                                    <li>Enhance early counting skills (1–10)</li>
                                    <li>Build a strong foundation for addition and subtraction</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Ask your child to count items in each group shown.</li>
                                    <li>Guide them to circle the group with <strong>“more” or “less.”</strong></li>
                                    <li>Use real objects like blocks, spoons, or fruits to recreate the questions.</li>
                                    <li>Say phrases like “Which plate has more?” or “Which bowl has less?” to build familiarity.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Big - Small</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Full - Empty</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Concept Is Important</h2>
                                <p>
                                    <strong>“More and Less”</strong> is not just about numbers — it’s about <strong>logical thinking and decision-making</strong>. Mastery of this concept supports early problem-solving and strengthens number comparison — skills that are key for math success in higher grades.
                                </p>
                            </article>
                            <h2>Which set has {questionType} {currentEmoji}?</h2>

                            <div style={{ display: "flex", justifyContent: "center", margin: "20px 0", width: "100%" }}>
                                <div className="setDiv" onClick={() => checkAnswer("A")} style={{ cursor: "pointer", display: "grid" }}>
                                    <span>Set A</span>
                                    <span>{setA.join(" ")}</span>
                                </div>
                                <div className="setBorder"></div>
                                <div className="setDiv" onClick={() => checkAnswer("B")} style={{ cursor: "pointer", display: "grid" }}>
                                    <span>Set B</span>
                                    <span>{setB.join(" ")}</span>
                                </div>
                            </div>

                            <p style={{ fontSize: "20px" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default MoreOrLessGame;
